/**
 * Stated visitors, in addition to the exit number (UAT MT-004). PURE.
 *
 * An exit study is fielded two ways at once: panelists whose phone recorded a
 * visit are surveyed as they leave (exit), and panelists reached by an ordinary
 * survey who say they visited the store in the window qualify too (stated).
 * Bid Desk always runs stated alongside exit, so an exit check estimates both
 * and answers with their sum, each with its own range. Stated is on by default
 * for exit and exit-within-24-hours checks (and trackers fielded as them), and
 * can be switched off.
 *
 * The stated group is the standard panel, run through a standard survey's
 * funnel, at the share who would say they visited times the study's own share
 * who qualify. That visited share is the person's, or else read from the visit
 * data: the share of panelists with location data who visited these stores in
 * a window as long as the field window.
 */
import { z } from 'zod';

import type { FunnelResult, Verdict } from './types';

/** Study types fielded with stated visitors alongside exit. */
export const EXIT_STUDY_TYPES: readonly string[] = ['exit_interview', 'exit_interview_24h'];

export const StatedSchema = z.object({
    /** No stated group. */
    off: z.boolean().optional(),
    /** The share of the panel who would say they visited, 0–1. Absent: read from the visit data. */
    share: z.number().gt(0).max(1).optional(),
    /** Completes wanted from stated visitors. Absent: as many as the window gives. */
    target: z.number().int().min(1).max(1_000_000).optional(),
});
export type Stated = z.infer<typeof StatedSchema>;

export interface EffectiveStated {
    share: number | null;
    target: number | null;
    byDefault: boolean;
}

/** `studyType` is the wave's: a tracker fielded as an exit interview takes stated visitors too. */
export function effectiveStated(r: { studyType: string; stated?: Stated }): EffectiveStated | null {
    if (!EXIT_STUDY_TYPES.includes(r.studyType) || r.stated?.off) return null;
    return {
        share: r.stated?.share ?? null,
        target: r.stated?.target ?? null,
        byDefault: !r.stated,
    };
}

/** What the stated group adds: its projection, capped at its own completes when it has some. */
export const statedAdds = (projected: number, target: number | null) =>
    target != null ? Math.min(projected, target) : projected;

/** The stated group's answer, kept beside the exit number. */
export interface StatedResult {
    projected: number;
    /** What it adds to the answer (capped at its completes). */
    adds: number;
    range: { low: number; high: number };
    target: number | null;
    /** Panelists the standard panel count found for the study's audience. */
    poolCount: number;
    /** The share who would say they visited, and where it came from. */
    share: number;
    shareSource: 'stated' | 'visit_data';
    /** The study's own share who qualify, applied on top. */
    incidenceRate: number;
}

/** The exit answer before stated was added, so both can be shown. */
export interface ExitPart {
    projected: number;
    range: { low: number; high: number };
    verdict: Verdict;
}

/**
 * Exit plus stated: the projection, range and accrual are the sums; the verdict
 * is read against the target as the funnel reads it, and never above what a
 * capped exit answer allowed (an unverified, stale or sensitive pool).
 */
export function withStated(
    exit: FunnelResult,
    stated: { result: FunnelResult; target: number | null },
    detail: Omit<StatedResult, 'projected' | 'adds' | 'range' | 'target'>,
    headroom: number,
): FunnelResult & { stated: StatedResult; exit: ExitPart } {
    const adds = statedAdds(stated.result.projected, stated.target);
    const scale = stated.result.projected > 0 ? adds / stated.result.projected : 0;
    const projected = exit.projected + adds;
    // The two groups miss independently: their distances from the middle add in
    // quadrature, not end to end (M4S-5930: summing the ends made the range as
    // wide as both groups missing the same way at once).
    const below = Math.hypot(
        exit.projected - exit.range.low,
        (stated.result.projected - stated.result.range.low) * scale,
    );
    const above = Math.hypot(
        exit.range.high - exit.projected,
        (stated.result.range.high - stated.result.projected) * scale,
    );
    const range = {
        low: Math.max(0, Math.floor(projected - below)),
        high: Math.ceil(projected + above),
    };
    const target = exit.target;
    let verdict: Verdict =
        target == null
            ? 'ceiling'
            : projected >= target * headroom
              ? 'feasible'
              : projected >= target
                ? 'marginal'
                : 'not_feasible';
    const capped = exit.warnings.some((w) =>
        ['eligibility_unverified', 'pool_stale', 'sensitive_targeting'].includes(w.code),
    );
    if (capped && verdict === 'feasible') verdict = 'marginal';
    // Day by day, the two groups fill together.
    const statedOn = (day: number) =>
        Math.round((stated.result.accrual.find((p) => p.day === day)?.cumulative ?? 0) * scale);
    const accrual = exit.accrual.map((p) => ({ ...p, cumulative: p.cumulative + statedOn(p.day) }));
    if (accrual.length) accrual[accrual.length - 1].cumulative = projected;
    return {
        ...exit,
        verdict,
        projected,
        range,
        accrual,
        targetReachedDay:
            target == null ? null : (accrual.find((p) => p.cumulative >= target)?.day ?? null),
        exit: { projected: exit.projected, range: exit.range, verdict: exit.verdict },
        stated: {
            ...detail,
            projected: stated.result.projected,
            adds,
            range: stated.result.range,
            target: stated.target,
        },
    };
}

const n = (x: number) => x.toLocaleString('en-US');
const pct = (x: number) => `${Math.round(x * 1000) / 10}%`;

/** The line the result shows under the answer. */
export function statedSentence(s: StatedResult, exit: ExitPart): string {
    const from =
        s.shareSource === 'visit_data'
            ? `${pct(s.share)} of panelists with location data visited these stores in a window this long, used as the share who would say they did`
            : `${pct(s.share)} of the panel would say they visited, as given`;
    const cap =
        s.target != null && s.projected > s.target
            ? `, capped at the ${n(s.target)} asked for`
            : '';
    return `Exit (recorded visits) about ${n(exit.projected)} (${n(exit.range.low)}–${n(exit.range.high)}), plus stated visitors about ${n(s.adds)}${cap} (${n(s.range.low)}–${n(s.range.high)}): panelists reached by survey who say they visited. ${from}, times the ${pct(s.incidenceRate)} who qualify.`;
}
